/**
 * Auto-saved map session: the URL query plus the in-memory layers (imported files and analysis
 * results) that the URL cannot bring back. Layers backed by a project dataset are left out: the
 * query (?dataset= / ?layers=) reloads those. Stored in IndexedDB (database `ultimate-gis`, store
 * `sessions`, key `last`); localStorage keeps `{ at, query }` so a cheap check needs no IndexedDB.
 */
export const SESSION_MAX_BYTES = 25 * 1024 * 1024;
export const AUTOSAVE_KEY = 'ultimate-gis:autosave';
export const SESSION_META_KEY = 'ultimate-gis:session:last';
export const RECENT_KEY = 'ultimate-gis:recent-maps';
export const RECENT_MAX = 8;
const DB_NAME = 'ultimate-gis';
const STORE = 'sessions';

const bytesOf = (geojson) => { try { return JSON.stringify(geojson || null).length; } catch { return 0; } };

/** Imported/result layers that the URL does not reload. */
export const isSessionImported = (l) => !!l && l.projectDatasetId === undefined && l.dataset?.id === undefined;
export const isSessionResult = (r) => !!r && r.projectDatasetId === undefined && r.dataset?.id === undefined && r.kind !== 'boreholes' && r.dataset?.kind !== 'boreholes';

/** Build a session object; drops the largest layers while the GeoJSON total is over the cap. */
export function serializeSession({ query = '', importedLayers = [], resultLayers = [], layerStyles = {} }, maxBytes = SESSION_MAX_BYTES, now = new Date()) {
  const imported = importedLayers.filter(isSessionImported).map((l) => ({
    id: l.id, name: l.name, visible: l.visible !== false, opacity: l.opacity, geojson: l.geojson,
    ...(l.xyz ? { xyz: l.xyz } : {}), ...(l.activeZ ? { activeZ: l.activeZ } : {}),
  }));
  const results = resultLayers.filter(isSessionResult).map((r) => ({
    id: r.id, name: r.name, kind: r.kind, visible: r.visible !== false, opacity: r.opacity, geojson: r.geojson,
    ...(r.dataset ? { dataset: r.dataset } : {}), ...(r.source ? { source: r.source } : {}),
  }));
  const sized = [...imported, ...results].map((l) => ({ l, size: bytesOf(l.geojson) }));
  let total = sized.reduce((s, e) => s + e.size, 0);
  const dropped = [];
  const gone = new Set();
  [...sized].sort((a, b) => b.size - a.size).forEach((e) => {
    if (total <= maxBytes) return;
    total -= e.size; gone.add(e.l); dropped.push(e.l.name || e.l.id);
  });
  const keep = (list) => list.filter((l) => !gone.has(l));
  const droppedIds = new Set([...gone].map((l) => l.id));
  const styles = {};
  Object.entries(layerStyles || {}).forEach(([k, v]) => {
    if (![...droppedIds].some((id) => k.includes(id))) styles[k] = v;
  });
  const session = { v: 1, at: now.toISOString(), query: String(query).replace(/^\?/, ''), imported: keep(imported), results: keep(results), layerStyles: styles };
  if (dropped.length) session.dropped = dropped;
  return session;
}

/** A parsed session, or null when the value is not a v1 session. */
export function deserializeSession(raw) {
  let s = raw;
  if (typeof raw === 'string') { try { s = JSON.parse(raw); } catch { return null; } }
  if (!s || s.v !== 1 || typeof s.query !== 'string') return null;
  return { ...s, imported: Array.isArray(s.imported) ? s.imported : [], results: Array.isArray(s.results) ? s.results : [], layerStyles: s.layerStyles || {} };
}

export const sessionHasContent = (s) => !!s && (!!s.query || s.imported.length > 0 || s.results.length > 0);

// ─── IndexedDB (promise wrapper, no dependency) ───
function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB unavailable')); return; }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => { if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function withStore(mode, fn) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(req?.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally { db.close(); }
}

/** Store the session (IndexedDB) and its cheap `{ at, query }` marker (localStorage). Never throws. */
export async function saveSession(session) {
  try { await withStore('readwrite', (s) => s.put(session, 'last')); } catch { return false; }
  try { localStorage.setItem(SESSION_META_KEY, JSON.stringify({ at: session.at, query: session.query })); } catch { /* storage unavailable */ }
  return true;
}
/** The saved session or null. Never throws. */
export async function loadSession() {
  try { return deserializeSession(await withStore('readonly', (s) => s.get('last'))); } catch { return null; }
}
/** The localStorage marker `{ at, query }` or null (no IndexedDB needed). */
export function sessionMeta() {
  try { const m = JSON.parse(localStorage.getItem(SESSION_META_KEY) || 'null'); return m && typeof m.query === 'string' ? m : null; } catch { return null; }
}

export function autosaveEnabled() {
  try { return localStorage.getItem(AUTOSAVE_KEY) !== '0'; } catch { return true; }
}
export function setAutosaveEnabled(on) {
  try { localStorage.setItem(AUTOSAVE_KEY, on ? '1' : '0'); } catch { /* storage unavailable */ }
}

// ─── Recent maps ───
export function loadRecent() {
  try { const r = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); return Array.isArray(r) ? r.slice(0, RECENT_MAX) : []; } catch { return []; }
}
/** Newest first, de-duplicated by project+dataset, capped at 8. */
export function addRecent(list, entry, now = new Date()) {
  const e = { project: entry.project || null, dataset: entry.dataset || null, name: entry.name, at: now.toISOString() };
  const rest = (list || []).filter((x) => !((x.project || null) === e.project && (x.dataset || null) === e.dataset));
  return [e, ...rest].slice(0, RECENT_MAX);
}
export function recordRecent(entry) {
  if (!entry?.name || (!entry.project && !entry.dataset)) return;
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(addRecent(loadRecent(), entry))); } catch { /* storage unavailable */ }
}

// ─── Last project ───
/** The project of the saved session query, else the newest recent map with a project, else null. */
export function lastProjectFrom(meta, recent) {
  const fromQuery = meta?.query ? new URLSearchParams(meta.query).get('project') : null;
  if (fromQuery) return fromQuery;
  return (recent || []).find((r) => r && r.project)?.project || null;
}
/** The project the map worked on last (localStorage only). */
export function lastProjectId() {
  return lastProjectFrom(sessionMeta(), loadRecent());
}
/**
 * /3d for a project: the viewer lists the project's stored models and loads its DEM.
 * `dataset` limits the models to that survey's and fits the camera to them; `model` (a stored
 * inversion id) is selected and drives the camera fit; `focus` hands over the map's survey line.
 */
export function viewer3dPath(projectId, { dataset = null, model = null, focus = false } = {}) {
  const project = projectId || lastProjectId();
  const q = [];
  if (project) q.push(`project=${encodeURIComponent(project)}`);
  if (dataset) q.push(`dataset=${encodeURIComponent(dataset)}`);
  if (model) q.push(`model=${encodeURIComponent(model)}`);
  if (focus) q.push('focus=1');
  const s = q.join('&');
  return s ? `/3d?${s}` : '/3d';
}
